import { HttpHeaders } from '@angular/common/http';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	TemplateRef,
	ViewChild
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MigrationDetailsService, MigrationStatusType } from 'src/app/migration-details/migration-details.service';
import { MigrationResult } from 'src/app/migration-result/migration-result';
import { MigrationService } from 'src/app/migration-service/migration.service';
import { catchError } from 'rxjs/internal/operators/catchError';
import { of } from 'rxjs/internal/observable/of';
import { BentoAlertItemOptions } from '@bento/bento-ng';
import { getCompanyId } from '@onviobr/services/session';
import { MigrationReusableService } from '../migration-reusable.service';
import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { MigrationDetailsModalContentComponent } from '../migration-details-modal-content/migration-details-modal-content.component';
import { TableModel } from '../table/table-model';
import { MigrationSource } from '../migration-source.enum';
import { ReportDownloadService } from '../../migration-service/report-download.service';
import { first } from 'rxjs/operators';
import { MigrationStatus } from '../migration-status.enum';
import { DateTimeService } from '../../migration-date-time/date-time.service';

const { COMPLETED, ERROR, CANCELLED } = MigrationStatus;

interface MigrationInfoType {
	title: string;
	urlToGetItems: string;
}
@Component({
	selector: 'app-completed',
	templateUrl: './completed.component.html',
	styleUrls: ['./completed.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompletedComponent implements OnInit, OnDestroy {
	readonly TAX_COMPETITOR_MIGRATION_SOURCE = MigrationSource.TAXCOMPETITOR;
	alerts: BentoAlertItemOptions[] = [];
	migrationErrorAlerts: BentoAlertItemOptions[] = [];

	migrationResults: Array<MigrationResult>;
	hasNewAddedRecord = false;
	migrationHeaderColumns = [];
	showBusyloader: boolean;
	showCompletedBusyloader: boolean;
	companyId: string;
	migrationResultsUrl = '/api/migration-orchestrator/v1/results?companyId';
	migrationStatus = 'migrationStatus=COMPLETED&migrationStatus=ERROR&migrationStatus=CANCELLED';

	showDownloadReportBusyLoader = false;
	showDetailsBusyLoader = false;
	showAlertsBusyLoader = false;
	migrationDetailsItemId: string;
	public getCompanyId = getCompanyId;
	tableModel = new TableModel<MigrationResult>();

	@ViewChild('actionsColumnCell', { static: true }) actionsColumnCell: TemplateRef<any>;
	@ViewChild('applicationColumnCell', { static: true }) applicationColumnCell: TemplateRef<any>;
	@ViewChild('startedOnColumnCell', { static: true }) startedOnColumnCell: TemplateRef<any>;
	@ViewChild('statusColumnCell', { static: true }) statusColumnCell: TemplateRef<any>;
	@ViewChild('recordsMigratedColumnCell', { static: true }) recordsMigratedColumnCell: TemplateRef<any>;
	@ViewChild('exclusionsColumnCell', { static: true }) exclusionsColumnCell: TemplateRef<any>;

	// eslint-disable-next-line @angular-eslint/no-input-rename
	@Input('migrationHeaderColumns')
	set _migrationHeaderColumns(columns: []) {
		this.migrationHeaderColumns = columns;
	}

	/**
	 * Value that's going be used to sent as HTTP param to migration-orchestrator to filter results.
	 */
	@Input() migrationSource: MigrationSource;

	@Input() showActionsColumn = false;

	/**
	 * For Migration Dashboard the application name should show up,
	 * so this is set to true by Migration Dashboard.
	 * By default application name won't be shown
	 */
	@Input() showApplicationNameColumn = false;

	/**
	 * Migration Dashboard shows In progress inside card, so setting
	 * this flag to true will use only In progress table
	 * By default showOnlyTable will be false
	 */
	@Input() showOnlyTable = false;

	/**
	 * NetClient CS needs custom template to choose the portals, this
	 * template will be set by NetclientMigrationComponent
	 */
	@Input() customTemplate: TemplateRef <any>;

	/**
	 * Optional. By default the Client Center registrations link is hidden.
	 */
	@Input() showClientRegistrationLink = false;

	@Input() migrationDetailsInfoTitle = 'Records migrated';

	@Output() updateMigrationResult: EventEmitter<MigrationResult[]> = new EventEmitter<MigrationResult[]>();

	/**
	 * Emits when new migration with completed status were added.
	 */
	@Output() newCompletedMigrationWasAdded:  EventEmitter<MigrationResult> = new EventEmitter<MigrationResult>();
	@Output() gotoProduct: EventEmitter<MigrationResult> = new EventEmitter<MigrationResult>();

	unsubscribe$ = new Subject();

	constructor(
		private translateService: TranslateService,
		private migrationDetails: MigrationDetailsService,
		private changeDetectorRef: ChangeDetectorRef,
		public migrationService: MigrationService,
		private reusableService: MigrationReusableService,
		public modalService: NgbModal,
		public reportDownloadService: ReportDownloadService,
		public dateTimeService: DateTimeService
	) {}

	async ngOnInit() {
		this.companyId = await this.getCompanyId();
		await this.setUpTable();
		await this.setMigrationResult();
		this.subscribeToUpdate();
	}

	private async setUpTable() {
		const tableModel = new TableModel<MigrationResult>();

		if (this.showActionsColumn) {
			tableModel.addColumn({
				columnHeaderText: this.translateService.instant('onvio.setup.migrations.status.completed.col.actions'),
				cellTemplate: this.actionsColumnCell
			});
		}

		if (this.showApplicationNameColumn) {
			const translationKey = 'onvio.setup.migrations.status.completed.col.application';
			tableModel.addColumn({
				columnHeaderText: this.translateService.instant(translationKey),
				cellTemplate: this.applicationColumnCell
			});
		}

		tableModel.addColumn({
			columnHeaderText: this.translateService.instant('onvio.setup.migrations.status.completed.col.startedOn'),
			cellTemplate: this.startedOnColumnCell
		}).addColumn({
			columnHeaderText: this.translateService.instant('onvio.setup.migrations.status.completed.col.status'),
			cellTemplate: this.statusColumnCell
		}).addColumn({
			columnHeaderText: this.translateService.instant('onvio.setup.migrations.status.completed.col.recordsMigrated'),
			cellTemplate: this.recordsMigratedColumnCell,
		}).addColumn({
			columnHeaderText: this.translateService.instant('onvio.setup.migrations.status.completed.col.exclusions'),
			cellTemplate: this.exclusionsColumnCell,
		});

		this.tableModel = tableModel;
	}

	ngOnDestroy() {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	subscribeToUpdate() {
		this.reusableService.refreshCompletedMigrations$.pipe(
			takeUntil(this.unsubscribe$)
		).subscribe(async (update) => {
			if (update) {
				this.hasNewAddedRecord = true;
				await this.getCompletedMigrationStatus();
			}
		});
	}

	async setMigrationResult() {
		const allCompletedList = this.migrationDetails.getCachedMigrationResults(this.migrationSource, MigrationStatusType.COMPLETED);
		if (allCompletedList?.length) {
			this.migrationResults = allCompletedList;
			this.tableModel.setItems(this.migrationResults);
			this.changeDetectorRef.markForCheck();
		} else {
			await this.getCompletedMigrationStatus();
		}
	}

	async getCompletedMigrationStatus() {
		this.showBusyloader = true;
		this.changeDetectorRef.markForCheck();
		try {
			const migrationResults = await this.getCompletedMigrations();
			if (migrationResults?.length) {
				this.migrationDetails.cacheMigrationResults(this.migrationSource, MigrationStatusType.COMPLETED, migrationResults);
				this.migrationResults = migrationResults;
				if (this.hasNewAddedRecord) {
					this.handleNewMigrationWasCompleted();
				}
			} else {
				this.migrationResults = null;
			}
			this.tableModel.setItems(this.migrationResults);
			this.showBusyloader = false;
			this.updateMigrationResult.emit(this.migrationResults);
			this.changeDetectorRef.markForCheck();
		} catch (err) {
			const errorMessage = this.translateService.instant(
				'onvio.setup.migrations.netfirm.http.error',
				{ action: 'Completed Migration Results', error: err }
			);
			this.showError(errorMessage);
			this.showBusyloader = false;
			this.changeDetectorRef.markForCheck();
		}
	}

	private getCompletedMigrations(): Promise<MigrationResult[]> {
		const headers = new HttpHeaders({
			'Content-Type': 'application/json'
		});
		return this.migrationService
			.get(`${this.migrationResultsUrl}=${this.companyId}&${this.migrationStatus}${this.getMigrationSourceParam()}`, headers)
			.toPromise<[MigrationResult]>();
	}

	private handleNewMigrationWasCompleted() {
		const newMigration = this.migrationResults[0];
		if (this.isNotMigrationDashboardTab()) {
			if (newMigration.migrationStatus === COMPLETED) {
				this.newCompletedMigrationWasAdded.emit(newMigration);
			} else if (newMigration.migrationStatus === ERROR) {
				this.displayErrorAlert();
			} else if (newMigration.migrationStatus === CANCELLED) {
				this.displayCanceledMigrationAlert(newMigration);
			}
		}
		this.hasNewAddedRecord = false;
	}

	private getMigrationSourceParam(): string {
		// migration source is applicable only for products; for dashboard, fetch all migration result
		if (this.migrationSource && this.isNotMigrationDashboardTab()) {
			return `&migrationSource=${this.migrationSource}`;
		} else {
			return '';
		}
	}

	private isNotMigrationDashboardTab() {
		return this.migrationSource !== MigrationSource.ALL;
	}

	showError(message: string, type: string = 'danger') {
		this.alerts.push({
			type,
			msg: message,
			closeable: true
		});
	}

	showMigratedRecordDetailsInfo(
		item: MigrationResult,
		completedMigrationContainerTemplate: HTMLElement) {
		const type: MigrationInfoType = {
			title: this.migrationDetailsInfoTitle,
			urlToGetItems: item.recordsMigratedRefLink
		};
		this.showDetailsBusyLoader = true;
		this.showMigratedRecordInfo(item, type, completedMigrationContainerTemplate);
	}

	showMigratedRecordAlertsInfo(
		item: MigrationResult,
		completedMigrationContainerTemplate: HTMLElement) {
		const type: MigrationInfoType = {
			title: 'Exclusions',
			urlToGetItems: item.alertRefLink
		};
		this.showAlertsBusyLoader = true;
		this.showMigratedRecordInfo(item, type, completedMigrationContainerTemplate);
	}

	private showMigratedRecordInfo(
		item: MigrationResult,
		type: MigrationInfoType,
		completedMigrationContainerTemplate: HTMLElement
	) {
		this.migrationDetailsItemId = item.migrationId;

		this.changeDetectorRef.markForCheck();

		const headers = new HttpHeaders({
			'Content-Type': 'application/json'
		});
		this.migrationService
			.get(type.urlToGetItems, headers)
			.pipe(
				catchError(err => {
					this.showResponseErrorMessage(err);
					this.hideMigrationInfoBusyLoader();
					this.migrationDetailsItemId = undefined;
					this.changeDetectorRef.markForCheck();
					return of([]);
				})
			).subscribe( (details: Array<any>) => {
				if (details?.length) {
					const options = {windowClass: 'completed-modal',
						container: completedMigrationContainerTemplate,
						ariaLabelledBy: 'modal-scrollable-title'};
					const modalRef = this.modalService.open(MigrationDetailsModalContentComponent, options);
					const component = modalRef.componentInstance as MigrationDetailsModalContentComponent;
					component.modalTitle = type.title;
					component.migrationRecordsDetails = details;
				}
				this.migrationDetailsItemId = undefined;
				this.hideMigrationInfoBusyLoader();
				this.changeDetectorRef.markForCheck();
			});
	}

	private hideMigrationInfoBusyLoader() {
		this.showDetailsBusyLoader = false;
		this.showAlertsBusyLoader = false;
	}

	downloadMigrationDetailsReport(item: MigrationResult): void {
		this.migrationDetailsItemId = item.migrationId;
		this.showDownloadReportBusyLoader = true;
		this.reportDownloadService.downloadMigrationResultsReport(item.migrationId)
			.pipe(first())
			.subscribe(() => {
			},
			(error) => {
				this.showResponseErrorMessage(error);
			}, () => {
				this.migrationDetailsItemId = undefined;
				this.showDownloadReportBusyLoader = false;
				this.changeDetectorRef.markForCheck();
			});
	}

	private showResponseErrorMessage(errorResponse): void {
		const errorMessage = this.translateService.instant('onvio.setup.migrations.netfirm.http.error',
			{action: 'Migrated Records Details', error: errorResponse});
		this.showError(errorMessage);
	}

	public displayErrorAlert(): void {
		this.migrationErrorAlerts.push({
			msg: this.translateService.instant('onvio.setup.migrations.status.completed.error'),
			type: 'warning',
			closeable: true
		});
	}

	private displayCanceledMigrationAlert(newMigration: MigrationResult): void {
		const startedDate = this.dateTimeService.getDateTimeFromUnixTimestamp(newMigration.migrationStartDate);
		const canceledDate = this.dateTimeService.getDateTimeFromUnixTimestamp(newMigration.migrationChangeDate);
		const msg = this.translateService.instant('onvio.setup.migrations.status.completed.cancelled', {
			startedDate,
			canceledDate,
			productName: newMigration.migrationSource
		});
		this.migrationErrorAlerts.push({
			msg,
			type: 'warning',
			closeable: true
		});
	}
}
